import { api } from '@/lib/axios';
import type {
  BoardWithSensors,
  CreateBoardPayload,
  TelemetryPoint,
  UpdateBoardPayload,
} from '@monitor/shared';

export async function listBoards(siteId?: number): Promise<BoardWithSensors[]> {
  const { data } = await api.get<BoardWithSensors[]>('/api/boards', {
    params: siteId != null ? { siteId } : undefined,
  });
  return data;
}

export async function findBoard(id: number): Promise<BoardWithSensors> {
  const { data } = await api.get<BoardWithSensors>(`/api/boards/${id}`);
  return data;
}

export async function createBoard(
  payload: CreateBoardPayload,
): Promise<BoardWithSensors> {
  const { data } = await api.post<BoardWithSensors>('/api/boards', payload);
  return data;
}

export async function updateBoard(
  id: number,
  payload: UpdateBoardPayload,
): Promise<BoardWithSensors> {
  const { data } = await api.patch<BoardWithSensors>(`/api/boards/${id}`, payload);
  return data;
}

export async function deleteBoard(id: number): Promise<void> {
  await api.delete(`/api/boards/${id}`);
}

/** Accept a quarantined foreign device publishing under this board's code
 *  (duplicate board_code). Rebinds the board to the new efuse UID so its
 *  telemetry is accepted from now on. */
export async function acceptBoardDeviceUid(id: number): Promise<BoardWithSensors> {
  const { data } = await api.post<BoardWithSensors>(`/api/boards/${id}/accept-device-uid`);
  return data;
}

/** Ignore a quarantined foreign device (keep the real binding, silence future
 *  uid_conflict alerts for that UID). */
export async function ignoreBoardDeviceUid(id: number): Promise<BoardWithSensors> {
  const { data } = await api.post<BoardWithSensors>(`/api/boards/${id}/ignore-device-uid`);
  return data;
}

/** Push a fan/relay mode change to the board over MQTT (auto/on/off), plus
 *  optional thermostat thresholds (auto mode). */
export async function setBoardFan(
  id: number,
  mode: 'auto' | 'on' | 'off',
  temps?: { onTemp?: number; offTemp?: number },
): Promise<void> {
  await api.post(`/api/boards/${id}/fan-control`, { mode, ...temps });
}

export interface BoardFanStatus {
  boardId: number;
  on: boolean;
  mode: 'auto' | 'on' | 'off';
  enabled?: boolean;
  relay: number;
  onTemp: number;
  offTemp: number;
  receivedAt: string;
}

/** Latest fan/relay state the board reported (null if none since restart). */
export async function getBoardFanStatus(
  id: number,
): Promise<BoardFanStatus | null> {
  const { data } = await api.get<BoardFanStatus | null>(
    `/api/boards/${id}/fan-status`,
  );
  return data;
}

// ─── Multi-relay automation (v0.16+ boards) ────────────────────────────────
export type RelayMode =
  | 'none'
  | 'temp'
  | 'schedule'
  | 'countdown'
  | 'cyclic'
  | 'sensor'
  | 'always_on'
  | 'always_off'
  | 'calendar';
export type RelayOverride = 'auto' | 'on' | 'off';

export interface BoardRelayState {
  ch: number;
  name: string;
  enabled?: boolean;
  on: boolean;
  mode: RelayMode;
  override: RelayOverride;
  tripped?: boolean;
  interlock?: number;
  maxOnSec?: number;
  remainSec?: number;
  // mode params (present depending on mode)
  onTemp?: number;
  offTemp?: number;
  onMin?: number;
  offMin?: number;
  days?: number;
  cyOn?: number;
  cyOff?: number;
  dur?: number;
  cdOff?: boolean; // countdown direction: false=on-then-off, true=off-then-on
  metric?: number;
  src?: number;
  op?: number;
  thr?: number;
  actOn?: boolean;
  inMode?: number; // 0=none,1=latch,2=timed,3=momentary
  inCh?: number;
  inTimer?: number;
  calOn?: number; // calendar on epoch (seconds)
  calOff?: number;
  curTemp?: number; // live temperature (temp mode)
  curVal?: number; // live metric value (sensor mode)
  offInSec?: number; // seconds until auto-off timer fires
}

export interface BoardRelayStatus {
  boardId: number;
  relays: BoardRelayState[];
  inputs?: number; // live 8-bit mask of physical input states
  receivedAt: string;
}

export interface RelayConfigPayload {
  enabled?: boolean;
  name?: string;
  autoMode?: RelayMode;
  onTemp?: number;
  offTemp?: number;
  onMin?: number;
  offMin?: number;
  days?: number;
  dur?: number;
  cyOn?: number;
  cyOff?: number;
  cdOff?: boolean;
  metric?: number;
  src?: number;
  op?: number;
  thr?: number;
  actOn?: boolean;
  maxOnSec?: number;
  interlock?: number;
  inMode?: number;
  inCh?: number;
  inTimer?: number;
  calOn?: number;
  calOff?: number;
}

export interface BoardLiveStatus {
  boardId: number;
  deviceUid?: string; // ESP32 efuse MAC (12 hex) — matches manage-web device_uid
  transport?: string; // 'ethernet' | 'wifi' | 'offline'
  ipAddress?: string;
  firmware?: string;
  rssi?: number;
  linkMbps?: number;
  mqttUp?: boolean;
  uptimeMs?: number;
  receivedAt: string;
  brokerConnected?: boolean; // authoritative MQTT presence from EMQX (keepalive)
  presenceAt?: string;
}

export async function getBoardLiveStatus(id: number): Promise<BoardLiveStatus | null> {
  const { data } = await api.get<BoardLiveStatus | null>(`/api/boards/${id}/live-status`);
  return data;
}

export async function getBoardRelays(id: number): Promise<BoardRelayStatus | null> {
  const { data } = await api.get<BoardRelayStatus | null>(`/api/boards/${id}/relays`);
  return data;
}

export async function setRelayOverride(
  id: number,
  ch: number,
  mode: RelayOverride,
): Promise<void> {
  await api.post(`/api/boards/${id}/relays/${ch}/override`, { mode });
}

export async function setRelayConfig(
  id: number,
  ch: number,
  config: RelayConfigPayload,
): Promise<void> {
  await api.post(`/api/boards/${id}/relays/${ch}/config`, config);
}

export async function pulseRelay(id: number, ch: number, seconds: number): Promise<void> {
  await api.post(`/api/boards/${id}/relays/${ch}/pulse`, { seconds });
}

export interface BoardSensorReading {
  sensorId: number;
  time: string;
  voltage: number | null;
  current: number | null;
  power: number | null;
  energy: number | null;
  temperature: number | null;
  humidity: number | null;
  raw: unknown;
}

export async function fetchBoardLatest(
  siteId: number,
  boardId: number,
): Promise<BoardSensorReading[]> {
  const { data } = await api.get<BoardSensorReading[]>('/api/telemetry/latest', {
    params: { siteId, boardId },
  });
  return data ?? [];
}

export async function fetchSensorLatest(
  siteId: number,
  sensorId: number,
): Promise<TelemetryPoint | null> {
  const { data } = await api.get<TelemetryPoint | null>('/api/telemetry/latest', {
    params: { siteId, sensorId },
  });
  return data;
}

// ─── Activity history (ประวัติบอร์ด) ────────────────────────────────────────
export type ActivityRange = '24h' | '7d' | '30d' | 'custom';
export interface ActivityCustom { from: string; to: string }

/** Build query params: a named range, or explicit from/to for 'custom'. */
function activityParams(range: ActivityRange, custom?: ActivityCustom): Record<string, string> {
  if (range === 'custom' && custom) return { from: custom.from, to: custom.to };
  return { range };
}

export interface CommandLogEntry {
  id: number;
  userId: number | null;
  username: string;
  action: string;         // relay_override | relay_config | relay_pulse | fan
  channel: number | null;
  params: Record<string, unknown> | null;
  at: string;
}

export interface RelayEventEntry {
  id: number;
  channel: number;
  name: string | null;
  on: boolean;
  mode: string | null;
  override: string | null;
  source: string | null;
  at: string;
}

export interface StatusEventEntry {
  id: number;
  status: 'online' | 'offline';
  reason: string | null;
  at: string;
}

export interface UptimeSummary {
  uptimePct: number | null;
  onlineSec: number;
  offlineSec: number;
  current: 'online' | 'offline' | null;
  incidents: { from: string; to: string; sec: number; reason: string | null; ongoing?: boolean }[];
}

export async function getCommandLogs(id: number, range: ActivityRange, custom?: ActivityCustom): Promise<CommandLogEntry[]> {
  const { data } = await api.get<CommandLogEntry[]>(`/api/boards/${id}/command-logs`, {
    params: { ...activityParams(range, custom), limit: 500 },
  });
  return data ?? [];
}

export async function getRelayEvents(id: number, range: ActivityRange, custom?: ActivityCustom): Promise<RelayEventEntry[]> {
  const { data } = await api.get<RelayEventEntry[]>(`/api/boards/${id}/relay-events`, { params: activityParams(range, custom) });
  return data ?? [];
}

export async function getStatusEvents(id: number, range: ActivityRange, custom?: ActivityCustom): Promise<StatusEventEntry[]> {
  const { data } = await api.get<StatusEventEntry[]>(`/api/boards/${id}/status-events`, { params: activityParams(range, custom) });
  return data ?? [];
}

export async function getUptimeSummary(id: number, range: ActivityRange, custom?: ActivityCustom): Promise<UptimeSummary> {
  const { data } = await api.get<UptimeSummary>(`/api/boards/${id}/uptime`, { params: activityParams(range, custom) });
  return data;
}

// ─── Board settings audit (ใครแก้ไขบอร์ด) ───────────────────────────────────
export interface BoardAuditEntry {
  id: number;
  userId: number | null;
  username: string;
  action: 'create' | 'update' | 'delete';
  changes: Record<string, { from: unknown; to: unknown }> | null;
  ip: string | null;
  at: string;
}

export async function getBoardAuditLogs(id: number): Promise<BoardAuditEntry[]> {
  const { data } = await api.get<BoardAuditEntry[]>(`/api/boards/${id}/audit-logs`);
  return data ?? [];
}
